import { useCallback, useEffect, useState } from "react";
import { Button, Checkbox, Icon, Input, Pill, Select, SkeletonText, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";

/**
 * Custom status, Do Not Disturb and quiet hours, on the profile page.
 * Everything here is the signed-in person's own; nothing is addressed by id.
 */

type OwnView = {
  status: { emoji: string | null; text: string | null; expiresAt: string | null } | null;
  dnd: { until: string } | null;
  quietHours: { enabled: boolean; start: string; end: string; workdaysOnly: boolean };
  timezone: string;
  quietNow: "dnd" | "quiet_hours" | null;
};

const CLEAR_AFTER = [
  { value: "30m", label: "30 minutes" },
  { value: "1h", label: "1 hour" },
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "never", label: "Don't clear" },
] as const;

const SNOOZE = [
  { value: "30m", label: "30 minutes" },
  { value: "1h", label: "1 hour" },
  { value: "2h", label: "2 hours" },
  { value: "tomorrow", label: "Until tomorrow morning" },
  { value: "custom", label: "Custom…" },
] as const;

const EMOJI = ["🗓️", "🍽️", "🏠", "🌴", "🤒", "🎧", "🚗", "💬"];

function formatUntil(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
}

/** The value a datetime-local input wants for "now + 1h", in the browser's zone. */
function defaultCustomUntil(): string {
  const d = new Date(Date.now() + 3_600_000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export function StatusSettings() {
  const { toast } = useToast();
  const [view, setView] = useState<OwnView | null>(null);
  const [emoji, setEmoji] = useState("");
  const [text, setText] = useState("");
  const [clearAfter, setClearAfter] = useState<(typeof CLEAR_AFTER)[number]["value"]>("1h");
  const [snooze, setSnooze] = useState<(typeof SNOOZE)[number]["value"]>("1h");
  const [customUntil, setCustomUntil] = useState(defaultCustomUntil);
  const [quiet, setQuiet] = useState<OwnView["quietHours"]>({ enabled: false, start: "22:00", end: "07:00", workdaysOnly: false });
  const [busy, setBusy] = useState(false);

  const apply = useCallback((next: OwnView) => {
    setView(next);
    setEmoji(next.status?.emoji ?? "");
    setText(next.status?.text ?? "");
    setQuiet(next.quietHours);
  }, []);

  useEffect(() => {
    api
      .get<OwnView>("/me/status")
      .then(apply)
      .catch(() => undefined);
  }, [apply]);

  const run = (request: () => Promise<OwnView>, done: string) => {
    setBusy(true);
    request()
      .then((next) => {
        apply(next);
        toast(done, { tone: "ok" });
      })
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not save that.", { tone: "danger" }))
      .finally(() => setBusy(false));
  };

  if (!view) {
    return (
      <section className="profile__card">
        <SkeletonText lines={3} />
      </section>
    );
  }

  return (
    <section className="profile__card" id="status">
      <div className="profile__card-head">
        <h2>
          <Icon name="bell" size={15} />
          Status and Do Not Disturb
        </h2>
        {view.quietNow && <Pill tone="warn">{view.quietNow === "dnd" ? "Do Not Disturb" : "Quiet hours"}</Pill>}
      </div>

      <h3 className="profile__subhead">Status</h3>
      <div className="profile__card-actions profile__emoji-row">
        {EMOJI.map((e) => (
          <Button key={e} size="sm" variant={emoji === e ? "secondary" : "ghost"} aria-label={`Use ${e}`} onClick={() => setEmoji(e)}>
            {e}
          </Button>
        ))}
      </div>
      <Input label="What are you up to?" value={text} maxLength={100} onChange={(e) => setText(e.target.value)} />
      <Select label="Clear after" value={clearAfter} onChange={(e) => setClearAfter(e.target.value as typeof clearAfter)}>
        {CLEAR_AFTER.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
      <div className="profile__card-actions">
        <Button
          disabled={busy || (!emoji && !text.trim())}
          onClick={() => run(() => api.put<OwnView>("/me/status", { emoji, text: text.trim(), clearAfter }), "Status set.")}
        >
          Save status
        </Button>
        {view.status && (
          <Button variant="ghost" disabled={busy} onClick={() => run(() => api.delete<OwnView>("/me/status"), "Status cleared.")}>
            Clear
          </Button>
        )}
      </div>
      {view.status?.expiresAt && <p className="profile__muted">Clears {formatUntil(view.status.expiresAt)}.</p>}

      <h3 className="profile__subhead">Do Not Disturb</h3>
      <p className="profile__muted">
        Pauses push, desktop notifications and email. In-app notifications still collect, and anything sent while paused is not
        delivered later.
      </p>
      {view.dnd ? (
        <div className="profile__card-actions">
          <span>On until {formatUntil(view.dnd.until)}.</span>
          <Button variant="secondary" disabled={busy} onClick={() => run(() => api.delete<OwnView>("/me/dnd"), "Do Not Disturb is off.")}>
            Turn off
          </Button>
        </div>
      ) : (
        <div className="profile__card-actions">
          <Select aria-label="Snooze for" value={snooze} onChange={(e) => setSnooze(e.target.value as typeof snooze)}>
            {SNOOZE.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {snooze === "custom" && (
            <input
              type="datetime-local"
              aria-label="Snooze until"
              value={customUntil}
              onChange={(e) => setCustomUntil(e.target.value)}
            />
          )}
          <Button
            disabled={busy}
            onClick={() =>
              run(
                () =>
                  api.put<OwnView>(
                    "/me/dnd",
                    snooze === "custom"
                      ? { duration: "custom", until: new Date(customUntil).toISOString() }
                      : { duration: snooze },
                  ),
                "Do Not Disturb is on.",
              )
            }
          >
            Snooze
          </Button>
        </div>
      )}

      <h3 className="profile__subhead">Quiet hours</h3>
      <p className="profile__muted">Every day in your timezone ({view.timezone.replace(/_/g, " ")}).</p>
      <label className="profile__check">
        <Checkbox
          checked={quiet.enabled}
          onChange={() => setQuiet((q) => ({ ...q, enabled: !q.enabled }))}
          aria-label="Turn on quiet hours"
        />
        Turn on quiet hours
      </label>
      <div className="profile__card-actions">
        <Input type="time" label="From" value={quiet.start} onChange={(e) => setQuiet((q) => ({ ...q, start: e.target.value }))} />
        <Input type="time" label="Until" value={quiet.end} onChange={(e) => setQuiet((q) => ({ ...q, end: e.target.value }))} />
      </div>
      <label className="profile__check">
        <Checkbox
          checked={quiet.workdaysOnly}
          onChange={() => setQuiet((q) => ({ ...q, workdaysOnly: !q.workdaysOnly }))}
          aria-label="Workdays only"
        />
        Workdays only (nights that begin Monday to Friday)
      </label>
      <div className="profile__card-actions">
        <Button
          disabled={busy || !quiet.start || !quiet.end}
          onClick={() => run(() => api.put<OwnView>("/me/quiet-hours", quiet), "Quiet hours saved.")}
        >
          Save quiet hours
        </Button>
      </div>
    </section>
  );
}
